import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Spin, Tooltip, message } from "antd";
import {
  getEmailThread,
  unlinkEmail,
  type EmailLinkType,
  type EmailMessageRecord,
  type RecordKey,
} from "../../utils/emailLinkAPI";
import {
  MessageCard,
  MessageListRow,
  OpenOutlookButton,
} from "./emailLinkShared";

// The full linked conversation for ONE portal record, in one place.
//
// Deliberately generic: it takes a record key and a label and knows nothing
// about quotes vs dealers. Part 1 renders it with linkType="quote", Part 2 with
// linkType="dealer" — that is the whole difference between the two tabs.
//
// Reads come from the portal's own indexed records, never Gmail, so the history
// keeps rendering during a Gmail outage.

export interface EmailThreadPanelProps {
  linkType: EmailLinkType;
  /** The quote number (Part 1) or model number (Part 2). */
  recordKey: string;
  /** Want-List Request ID, when the buy side has one. */
  wtbRequestId?: string;
  /** Human label for the key, e.g. "Quote" or "Model". */
  keyLabel: string;
  /** Bumped by the parent to force a refetch after an attach/confirm. */
  refreshToken?: number;
  /** Filters the loaded thread by sender/subject, client-side. */
  search?: string;
  /** Inclusive date range (by sent/received date), applied client-side. */
  dateFrom?: Date | null;
  dateTo?: Date | null;
  /**
   * Hands the selected message's actions — Open in Email, Open Outlook and
   * Unlink — to the page shell, which renders them in the tab bar so the
   * reading pane keeps its full height.
   */
  onActions?: (actions: React.ReactNode) => void;
  /** The loaded conversation's exact message count, for the tab badge. */
  onCount?: (total: number) => void;
  /**
   * Shrink the reading pane's header chrome. On the quote page's email tabs
   * there is a fraction of the height the Emails page has, so the message
   * body needs every pixel the header can give back.
   */
  compact?: boolean;
}

const EmailThreadPanel: React.FC<EmailThreadPanelProps> = ({
  linkType,
  recordKey,
  wtbRequestId,
  keyLabel,
  refreshToken = 0,
  search = "",
  dateFrom = null,
  dateTo = null,
  onActions,
  onCount,
  compact = false,
}) => {
  const [messages, setMessages] = useState<EmailMessageRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const queryKey: RecordKey | null = useMemo(() => {
    if (linkType === "quote") {
      return recordKey ? { linkType: "quote", quoteId: recordKey } : null;
    }
    if (!recordKey && !wtbRequestId) return null;
    return { linkType: "dealer", model: recordKey || undefined, wtbRequestId };
  }, [linkType, recordKey, wtbRequestId]);

  // The buy-side picker can carry several models at once (comma-joined), so
  // the empty state below can say how many it searched across.
  const recordKeyCount = recordKey
    ? recordKey.split(",").filter(Boolean).length
    : 0;

  const load = useCallback(async () => {
    if (!queryKey) {
      setMessages([]);
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const response = await getEmailThread(queryKey);
      setMessages(response.messages);

      // Default to the newest message — that is what you actually want to read.
      setSelectedId((current) => {
        if (current && response.messages.some((item) => item.id === current)) {
          return current;
        }
        return response.messages.length
          ? response.messages[response.messages.length - 1].id
          : null;
      });
    } catch (error: any) {
      setLoadError(
        error?.response?.data?.message ||
          error?.message ||
          "Could not load the linked conversation",
      );
    } finally {
      setLoading(false);
    }
  }, [queryKey]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  // Reported as a plain number so a parent holding this in state settles
  // after one pass — handing back a fresh object every load would keep
  // re-rendering this panel's own callback prop.
  const messageCount = messages.length;
  useEffect(() => {
    onCount?.(queryKey ? messageCount : 0);
  }, [onCount, queryKey, messageCount]);

  const selected = messages.find((item) => item.id === selectedId) || null;

  // Groups replies that belong to the same conversation together, so a
  // back-and-forth reads as one thread instead of N unrelated-looking rows.
  // Subject match comes FIRST, not Gmail's own conversationId: a quote resend
  // or a reply composed outside a real Gmail "Reply" action drops the
  // In-Reply-To/References headers, so Gmail hands it a brand new thread id
  // even though it plainly belongs with the others. Every message here is
  // already scoped to one quote/model, so grouping by subject alone (with
  // "Re:"/"Fwd:" stripped) is safe and catches what conversationId misses.
  // `messages` already arrives in chronological order, preserved here both
  // across and within groups.
  const normalizeSubjectKey = (subject: string | null | undefined) => {
    let value = String(subject || "").trim();
    let previous;
    do {
      previous = value;
      value = value.replace(/^(re|fwd?|fw)\s*:\s*/i, "").trim();
    } while (value !== previous);
    return value.toLowerCase();
  };

  const fromTime = dateFrom
    ? new Date(
        dateFrom.getFullYear(),
        dateFrom.getMonth(),
        dateFrom.getDate(),
      ).getTime()
    : null;
  const toTime = dateTo
    ? new Date(
        dateTo.getFullYear(),
        dateTo.getMonth(),
        dateTo.getDate() + 1,
      ).getTime() - 1
    : null;

  const filteredMessages = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term && fromTime === null && toTime === null) return messages;
    return messages.filter((record) => {
      if (fromTime !== null || toTime !== null) {
        const when = new Date(record.sentAt || record.receivedAt || 0).getTime();
        if (fromTime !== null && when < fromTime) return false;
        if (toTime !== null && when > toTime) return false;
      }
      if (!term) return true;
      return [record.subject, record.fromEmail, record.fromName]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term));
    });
  }, [messages, search, fromTime, toTime]);

  // Keep the reading pane inside the filtered set — otherwise a message outside
  // the chosen date range / search stays open on the right after filtering.
  useEffect(() => {
    if (!filteredMessages.length) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!filteredMessages.some((item) => item.id === selectedId)) {
      setSelectedId(filteredMessages[filteredMessages.length - 1].id);
    }
  }, [filteredMessages, selectedId]);

  const threads = useMemo(() => {
    const byKey = new Map<string, EmailMessageRecord[]>();
    for (const record of filteredMessages) {
      const key =
        normalizeSubjectKey(record.subject) ||
        record.conversationId ||
        `single-${record.id}`;
      const bucket = byKey.get(key);
      if (bucket) bucket.push(record);
      else byKey.set(key, [record]);
    }
    // Newest first: mails inside a thread, and the threads themselves by their
    // latest mail. `messages` stays chronological for everything else.
    const timeOf = (record: EmailMessageRecord) =>
      new Date(record.sentAt || record.receivedAt || 0).getTime();
    return Array.from(byKey.values())
      .map((bucket) => [...bucket].sort((a, b) => timeOf(b) - timeOf(a)))
      .sort((a, b) => timeOf(b[0]) - timeOf(a[0]));
  }, [filteredMessages]);

  const handleUnlink = async (record: EmailMessageRecord) => {
    if (!record.link) return;
    try {
      await unlinkEmail(record.link.id);
      message.success("Email removed from this record");
      await load();
    } catch (error: any) {
      error?.response?.data?.message
        ? message.error(error.response.data.message)
        : message.error("Could not unlink the email");
    }
  };

  // The selected message's actions, rendered by the page shell in the tab bar
  // rather than as a command-bar strip inside the card.
  const actions = selected ? (
    <div className="flex flex-wrap items-center gap-1.5">
      <OpenOutlookButton record={selected} size="middle" />
      {selected.link ? (
        <Tooltip title="Remove this email from this record">
          <Button danger onClick={() => handleUnlink(selected)}>
            Unlink
          </Button>
        </Tooltip>
      ) : null}
    </div>
  ) : null;

  useEffect(() => {
    onActions?.(actions);
    // Follows the state the node reads, not the node's identity, or it loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onActions, selected]);

  if (!queryKey) {
    return (
      <Empty
        description={`Pick a ${keyLabel.toLowerCase()} to see its email history`}
        className="!py-16"
      />
    );
  }

  return (
    <div data-email-drop-zone={compact ? "" : undefined} className="flex h-full min-h-0 flex-col gap-3">
      {/* No header row here — the record label, counts and Refresh are rendered
          by the parent in the tab bar via onThreadState. */}
      {loadError ? (
        <Alert
          type="error"
          showIcon
          message="Could not load the conversation"
          description={loadError}
        />
      ) : null}

      <Spin
        spinning={loading}
        wrapperClassName={
          "flex min-h-0 flex-1 flex-col " +
          "[&_.ant-spin-container]:flex [&_.ant-spin-container]:min-h-0 " +
          "[&_.ant-spin-container]:flex-1 [&_.ant-spin-container]:flex-col"
        }
      >
        {!messages.length && !loading ? (
          <Empty
            className="!py-16"
            description={
              <span>
                {recordKeyCount > 1
                  ? `No emails are linked to any of these ${recordKeyCount} ${keyLabel.toLowerCase()}s yet.`
                  : `No emails are linked to this ${keyLabel.toLowerCase()} yet.`}
                <br />
                <span className="text-xs text-gray-400">
                  Confirmed links only — check Needs confirm for matches
                  waiting on a review, or attach one from the Inbox tab.
                </span>
              </span>
            }
          />
        ) : !filteredMessages.length && !loading ? (
          <Empty className="!py-16" description="No emails match your filters" />
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[240px_1fr] xl:grid-cols-[260px_1fr] 2xl:grid-cols-[300px_1fr]">
            {/* Chronological list — the complete history for this record,
                styled as an Outlook message list. */}
            <div className="max-h-[55vh] divide-y divide-gray-100 overflow-auto rounded-lg border border-gray-200 bg-white lg:max-h-none lg:min-h-0">
              {threads.map((thread) => {
                const row = (record: EmailMessageRecord) => (
                  <MessageListRow
                    key={record.id}
                    record={record}
                    active={record.id === selectedId}
                    onClick={() => setSelectedId(record.id)}
                    compact={compact}
                    note={
                      record.link?.matchSource === "manual"
                        ? "attached manually"
                        : undefined
                    }
                  />
                );

                if (thread.length === 1) return row(thread[0]);

                // A real back-and-forth: group it under one wrapper so it
                // reads as a single conversation rather than separate mail.
                // Keyed by the same value the group is built from: one Gmail
                // thread whose reply subjects differ (extra spaces, "AW:",
                // "[EXTERNAL]") splits into several groups that share a
                // conversationId, and duplicate keys left filtered-out rows
                // on screen after a date/search filter.
                const groupKey = `thread-${
                  normalizeSubjectKey(thread[0].subject) ||
                  thread[0].conversationId ||
                  `single-${thread[0].id}`
                }`;
                return (
                  <div key={groupKey} className="bg-gray-50/60">
                    <div className="divide-y divide-gray-100 pl-2">
                      {thread.map(row)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Reader: body, attachments, Open in Gmail, reply / reply-all. */}
            <div className="min-h-0 overflow-auto lg:overflow-hidden lg:pr-1">
              {selected ? (
                <MessageCard
                  record={selected}
                  fill
                  compact={compact}
                  showCommandBar={false}
                  onReplySent={load}
                />
              ) : (
                <Empty description="Select a message" className="!py-16" />
              )}
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default EmailThreadPanel;
